import React, { useEffect, useState, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";

import { 
  ShieldCheck, 
  AlertTriangle, 
  Activity, 
  Globe, 
  Bitcoin,
  Landmark,
  Skull,
  Key,
  Copy,
  Loader2,
  Terminal,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  UserCheck, 
  Phone,
  CreditCard,
  Fingerprint,
  MapPin,
  Sparkles,
  Download,
  FileDown,
  Eye,
  EyeOff
} from "lucide-react";

// --- UI CONFIGURATION ---
const SECTION_BG = "bg-[#0a1224]/80 backdrop-blur-md border border-white/5 shadow-xl";
const TABLE_HEADER_BG = "bg-white/[0.03] border-b border-white/5";

// --- HELPER FUNCTIONS ---
const getRiskColor = (score) => {
  if (score >= 85) return "text-red-500";
  if (score >= 60) return "text-orange-500";
  if (score >= 35) return "text-yellow-500";
  return "text-emerald-500"; 
};

const getFindingMeta = (type) => {
    const t = type?.toLowerCase() || "";
    if (t.includes('malicious') || t.includes('dark')) return { icon: <Skull className="w-4 h-4 text-red-500" />, label: "Dark Web / Malicious", severity: "Critical", score: 5 };
    if (t.includes('gov') || t.includes('mil')) return { icon: <Landmark className="w-4 h-4 text-amber-500" />, label: "Government Assets", severity: "High", score: 4 };
    if (t.includes('vuln') || t.includes('vault') || t.includes('password')) return { icon: <Key className="w-4 h-4 text-red-400" />, label: "Password Vault", severity: "Critical", score: 4.5 };
    if (t.includes('infra') || t.includes('cloud')) return { icon: <Activity className="w-4 h-4 text-sky-400" />, label: "Corp Infrastructure", severity: "High", score: 3 };
    if (t.includes('finance') || t.includes('bank')) return { icon: <CreditCard className="w-4 h-4 text-emerald-400" />, label: "Banking / Fintech", severity: "High", score: 3 };
    if (t.includes('crypto')) return { icon: <Bitcoin className="w-4 h-4 text-yellow-400" />, label: "Cryptocurrency", severity: "Medium", score: 2 };
    if (t.includes('edu')) return { icon: <Activity className="w-4 h-4 text-violet-400" />, label: "Educational / Academic", severity: "Medium", score: 2.5 };
    if (t.includes('geo')) return { icon: <Globe className="w-4 h-4 text-blue-400" />, label: "Geographic Anomaly", severity: "Medium", score: 2 };
    return { icon: <AlertTriangle className="w-4 h-4 text-neutral-400" />, label: "General Anomaly", severity: "Low", score: 1 };
};

/* ================= SUB-COMPONENTS ================= */

const FormattedSummary = ({ text }) => {
    if (!text) return null;
    const parts = text.split(/(CRITICAL:|URGENT:|WARNING:)/g);
    return (
        <div className="space-y-4 text-sm text-neutral-300 leading-relaxed font-light">
            {parts.map((part, i) => {
                const cleanPart = part.trim();
                if (!cleanPart || ["CRITICAL:", "URGENT:", "WARNING:"].includes(cleanPart)) return null;
                const prev = parts[i-1]?.trim();
                if (["CRITICAL:", "URGENT:", "WARNING:"].includes(prev)) {
                    return (
                        <div key={i} className="flex items-start gap-4 bg-red-500/5 border-l-2 border-red-500 p-4 rounded-r-lg my-2">
                            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
                            <div>
                                <span className="text-xs font-bold text-red-400 uppercase tracking-widest block mb-1">High Priority Intelligence</span>
                                <span className="text-white font-medium">{cleanPart}</span>
                            </div>
                        </div>
                    );
                }
                return <p key={i}>{cleanPart}</p>;
            })}
        </div>
    );
};

const IdentityProfile = ({ identities }) => {
    const hasNames = identities?.names && identities.names.length > 0;
    const hasPhones = identities?.phones && identities.phones.length > 0;
    const hasGovIds = identities?.gov_ids && identities.gov_ids.length > 0;
    if (!hasNames && !hasPhones && !hasGovIds) return null;
    return (
        <div className={`${SECTION_BG} rounded-md p-5 border-l-2 border-sky-500`}>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-white/5">
                <UserCheck className="w-4 h-4 text-sky-400" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wide">WhiteIntel Identity Resolution</h3>
            </div>
            <div className="space-y-4">
                {hasNames && (
                    <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                        <span className="text-[10px] text-neutral-500 uppercase font-semibold">Potential Real Names</span>
                        <div className="flex flex-wrap gap-2">
                            {identities.names.map((name, i) => (
                                <span key={i} className="px-2 py-0.5 bg-sky-500/10 text-sky-300 text-[10px] rounded border border-sky-500/20 font-mono">{name}</span>
                            ))}
                        </div>
                    </div>
                )}
                {hasPhones && (
                    <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                        <span className="text-[10px] text-neutral-500 uppercase font-semibold flex items-center gap-1.5"><Phone className="w-3 h-3" /> Potential Phone Numbers</span>
                        <div className="flex flex-wrap gap-2">
                            {identities.phones.map((phone, i) => (
                                <span key={i} className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 text-[10px] rounded border border-emerald-500/20 font-mono">{phone}</span>
                            ))}
                        </div>
                    </div>
                )}
                {hasGovIds && (
                    <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                        <span className="text-[10px] text-neutral-500 uppercase font-semibold flex items-center gap-1.5"><Fingerprint className="w-3 h-3 text-rose-400" /> Potential National IDs</span>
                        <div className="flex flex-wrap gap-2">
                            {identities.gov_ids.map((id, i) => (
                                <span key={i} className="px-2 py-0.5 bg-rose-500/10 text-rose-300 text-[10px] rounded border border-rose-500/20 font-mono tracking-wider">{id}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const CountryProfile = ({ countries }) => {
    if (!countries || countries.length === 0) return null;
    return (
        <div className={`${SECTION_BG} rounded-md p-5 border-l-2 border-indigo-500`}>
            <div className="flex items-center gap-2 mb-4 pb-2 border-b border-white/5">
                <Globe className="w-4 h-4 text-indigo-400" />
                <h3 className="text-xs font-bold text-white uppercase tracking-wide">Geo-Location Attribution</h3>
            </div>
            <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto pr-2 custom-scrollbar">
                {countries.map((country, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 bg-indigo-500/10 text-indigo-200 text-[10px] rounded border border-indigo-500/20">
                        <MapPin className="w-3 h-3 text-indigo-400" />
                        <span className="font-mono">{country}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AnomaliesTable = ({ findings }) => {
    if (!findings?.length) return <div className="p-8 text-center text-neutral-500 text-sm italic">No specific threat vectors identified.</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
                <thead className={TABLE_HEADER_BG}>
                    <tr className="text-neutral-500 text-[11px] uppercase tracking-wider font-semibold">
                        <th className="px-6 py-3">Risk Vector</th>
                        <th className="px-6 py-3">Evidence</th>
                        <th className="px-6 py-3 text-right">Severity</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/5 text-neutral-300">
                    {findings.map((item, i) => {
                        const meta = getFindingMeta(item.type);
                        return (
                            <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                                <td className="px-6 py-3 whitespace-nowrap font-medium text-white flex items-center gap-3">
                                    <div className="p-1 bg-neutral-900 border border-white/5 rounded">{meta.icon}</div>
                                    {meta.label}
                                </td>
                                <td className="px-6 py-3 font-mono text-xs text-neutral-400">{item.text}</td>
                                <td className="px-6 py-3 text-right">
                                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${meta.severity === 'Critical' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-sky-500/10 text-sky-400 border-sky-500/20'}`}>
                                        {meta.severity.toUpperCase()}
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const CredRow = ({ item, forceReveal }) => {
    const [revealed, setRevealed] = useState(false);
    useEffect(() => { setRevealed(forceReveal); }, [forceReveal]);
    const copy = (text) => navigator.clipboard.writeText(text);
    const getTypeStyle = (type) => {
        switch(type) {
            case 'Malicious': return 'bg-red-500/10 text-red-400 border-red-500/20';
            case 'Gov': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
            case 'Vault': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
            case 'Infra': return 'bg-sky-500/10 text-sky-400 border-sky-500/20';
            case 'Finance': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
            default: return 'bg-neutral-500/10 text-neutral-400 border-neutral-500/20';
        }
    };
    return (
        <tr className="hover:bg-white/[0.02] transition-colors group">
            <td className="px-6 py-3">
                <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase tracking-widest ${getTypeStyle(item.type)}`}>{item.type || 'General'}</span>
            </td>
            <td className="px-6 py-3 font-medium text-white">{item.app}</td>
            <td className="px-6 py-3 font-mono text-xs text-neutral-400">{item.username}</td>
            <td className="px-6 py-3">
                <div onClick={() => setRevealed(!revealed)} className={`font-mono text-xs px-3 py-1 rounded cursor-pointer w-fit transition-all border ${revealed ? "bg-red-500/10 text-red-200 border-red-500/20" : "bg-white/5 text-transparent blur-[4px] border-transparent"}`}>
                    {item.password || "NO_DATA"}
                </div>
            </td>
            <td className="px-6 py-3 text-right">
                <button onClick={() => copy(item.password)} className="p-1.5 text-neutral-500 hover:text-white bg-transparent hover:bg-white/5 rounded transition-colors"><Copy className="w-3.5 h-3.5" /></button>
            </td>
        </tr>
    );
};

const CriticalCredTable = ({ assets, query }) => {
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [showAllPasswords, setShowAllPasswords] = useState(false);
    const [filterType, setFilterType] = useState('All');
    const ITEMS_PER_PAGE = 5;

    const categories = useMemo(() => {
        const types = assets?.map(a => a.type || 'General') || [];
        return ['All', ...new Set(types)];
    }, [assets]);

    const filteredAndSortedAssets = useMemo(() => {
        if (!assets) return [];
        let result = [...assets];
        if (filterType !== 'All') {
            result = result.filter(a => (a.type || 'General') === filterType);
        }
        return result.sort((a, b) => {
            const scoreA = getFindingMeta(a.type).score;
            const scoreB = getFindingMeta(b.type).score;
            return sortOrder === 'desc' ? scoreB - scoreA : scoreA - scoreB;
        });
    }, [assets, sortOrder, filterType]);

    const handleExportCsv = () => {
        if (!filteredAndSortedAssets.length) return;
        const headers = ["Risk Category", "Application", "Identity", "Secret"];
        const rows = filteredAndSortedAssets.map(item => [
            item.type || "General",
            item.app || "N/A",
            item.username || "N/A",
            item.password || "N/A",
        ]);
        const csvContent = [headers.join(","), ...rows.map(row => row.map(f => `"${String(f).replace(/"/g, '""')}"`).join(","))].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `Critical_Intelligence_${query}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalPages = Math.ceil(filteredAndSortedAssets.length / ITEMS_PER_PAGE);
    const paginatedAssets = filteredAndSortedAssets.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    useEffect(() => setCurrentPage(1), [sortOrder, filterType]);

    if (!assets?.length) return null;

    return (
        <div className={`mt-6 ${SECTION_BG} rounded-md overflow-hidden flex flex-col`}>
             <div className="px-6 py-4 border-b border-white/5 bg-red-500/5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <h3 className="text-xs font-bold text-red-400 flex items-center gap-2 uppercase tracking-widest"><AlertTriangle className="w-4 h-4" /> Critical Credentials</h3>
                    <span className="text-[10px] bg-red-500/20 text-red-300 px-2 py-0.5 rounded border border-red-500/20">{filteredAndSortedAssets.length} Assets</span>
                    <span className="text-[10px] bg-gray-500/20 text-red-300 px-2 py-0.5 rounded border border-red-500/20">For full credentials see Credentials tab.</span>
                </div>
                <div className="flex items-center gap-2">
                    <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="bg-[#000414] text-[10px] font-bold uppercase text-sky-400 border border-white/10 rounded px-2 py-1.5 outline-none cursor-pointer">
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                    <button onClick={handleExportCsv} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-medium text-sky-400 transition-colors"><FileDown className="w-3 h-3" /> Export CSV</button>
                    <button onClick={() => setShowAllPasswords(!showAllPasswords)} className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-medium text-neutral-300 transition-colors">{showAllPasswords ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className={TABLE_HEADER_BG}>
                        <tr className="text-neutral-500 text-[10px] uppercase tracking-widest font-black">
                            <th className="px-6 py-3 cursor-pointer" onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}>Risk Category <ArrowUpDown className="inline w-3 h-3 ml-1" /></th>
                            <th className="px-6 py-3">Application</th>
                            <th className="px-6 py-3">Identity</th>
                            <th className="px-6 py-3">Secret</th>
                            <th className="px-6 py-3 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5 text-neutral-300">
                        {paginatedAssets.map((item, i) => (<CredRow key={`${i}-${currentPage}`} item={item} forceReveal={showAllPasswords} />))}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-white/5 bg-white/[0.02] flex items-center justify-between">
                    <span className="text-[10px] text-neutral-500">Page {currentPage} of {totalPages}</span>
                    <div className="flex items-center gap-2">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} className="p-1 rounded hover:bg-white/10 disabled:opacity-20 transition-all"><ChevronLeft className="w-4 h-4 text-neutral-400" /></button>
                        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} className="p-1 rounded hover:bg-white/10 disabled:opacity-20 transition-all"><ChevronRight className="w-4 h-4 text-neutral-400" /></button>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ================= MAIN EXPORTED COMPONENT ================= */

export default function InvestigationInsights({ query, jobId, status }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();

  // PDF Export Logic
 const handleClientPdfExport = () => {
    if (!data) return;
    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18); doc.text("Whiteintel AI Analyst Report", 14, 20);
    doc.setFontSize(10); doc.text(`Target: ${query}`, 14, 28);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 34);
    doc.line(14, 38, 196, 38);
    
    // Risk Metrics
    doc.setFontSize(12); doc.text(`Risk Score: ${data.risk_score} / 100 (${data.risk_level})`, 14, 48);
    
    // --- NEW: IDENTITY RESOLUTION SECTION ---
// --- UPDATED IDENTITY RESOLUTION SECTION ---
let identityY = 58;
const identities = data.suspected_identities;

if (identities?.names?.length || identities?.phones?.length || identities?.gov_ids?.length) {
    doc.setFontSize(13);
    doc.setTextColor(0, 102, 204);
    doc.text("WhiteIntel Identity Resolution", 14, identityY);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    
    identityY += 8;
    
    const renderWrappedField = (label, values) => {
        if (!values || values.length === 0) return;
        doc.setFont(undefined, 'bold');
        doc.text(`${label}:`, 14, identityY);
        doc.setFont(undefined, 'normal');
        
        // Wrap the joined string to fit within 130mm (leaves room for the label)
        const joinedText = values.join(", ");
        const wrappedText = doc.splitTextToSize(joinedText, 130); 
        
        doc.text(wrappedText, 55, identityY);
        // Advance Y based on how many lines the text took
        identityY += (wrappedText.length * 5) + 2; 
    };

    renderWrappedField("Potential Names", identities.names);
    renderWrappedField("Potential Phones", identities.phones);
    renderWrappedField("Potential National IDs", identities.gov_ids);
    
    identityY += 5; 
}

    // Executive Summary
    doc.setFontSize(12); doc.setFont(undefined, 'bold');
    doc.text("Executive Summary", 14, identityY);
    doc.setFont(undefined, 'normal'); doc.setFontSize(10);
    const splitSummary = doc.splitTextToSize(data.summary || "No summary available.", 180);
    doc.text(splitSummary, 14, identityY + 8);
    
    // Tables
    let tableStartY = identityY + 8 + (splitSummary.length * 5) + 10;
    
    autoTable(doc, {
        startY: tableStartY, 
        head: [['Type', 'Detail']], 
        body: (data.key_findings || []).map(f => [f.type.toUpperCase(), f.text]),
        theme: 'striped', 
        headStyles: { fillColor: [22, 163, 74] } 
    });

    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10, 
        head: [['Category', 'App', 'Identity', 'Secret']],
        body: (data.critical_assets || []).slice(0, 500).map(c => [c.type, c.app, c.username, c.password]),
        theme: 'grid', 
        headStyles: { fillColor: [220, 38, 38] }
    });

    doc.save(`WhiteIntel_Report_${query}.pdf`);
  };
  // Fetch the actual AI JSON data
  const fetchAIData = async (id, token) => {
    try {
      const res = await fetch("https://whiteintel.io/api/regular/app/v2/investigations/get_job_ai.php", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ job_id: id }),
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
        setLoading(false);
      } else {
        throw new Error(json.error || "Failed to load neural report.");
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // ONLY fetch data when the parent says the status is 'completed'
  useEffect(() => {
    if (jobId && status === 'completed' && !data) {
      const token = localStorage.getItem("token") || "";
      fetchAIData(jobId, token);
    }
  }, [jobId, status, data]);

  // Handle Loading/Processing states based on parent status
  if (status === 'pending' || status === 'processing' || (status === 'completed' && loading)) {
    return (
      <div className="h-full flex flex-col items-center justify-center space-y-6 min-h-[400px]">
          <div className="relative">
              <div className="absolute inset-0 bg-sky-500/20 blur-xl rounded-full animate-pulse" />
              <Loader2 className="w-12 h-12 text-sky-400 animate-spin relative z-10" />
          </div>
          <div className="text-center">
              <h3 className="text-lg font-medium text-white">
                {status === 'completed' ? "Assembling Neural Report..." : "Neural Models Active..."}
              </h3>
              <p className="text-xs text-neutral-500">Cross-referencing dark web datasets...</p>
          </div>
      </div>
    );
  }

  if (status === 'failed' || error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6 min-h-[400px]">
          <AlertTriangle className="w-16 h-16 text-red-500 mb-4 opacity-50" />
          <h3 className="text-lg font-medium text-white">Retrieval Failed</h3>
          <p className="text-neutral-400 text-sm mt-2">{error || "AI Analysis failed"}</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="h-full overflow-y-auto custom-scrollbar p-1 space-y-6 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
         <div>
             <h2 className="text-xl font-bold text-white flex items-center gap-2">
                 <Sparkles className="w-5 h-5 text-sky-400" />
                 Whiteintel Neural Analysis
             </h2>
             <p className="text-[10px] text-neutral-500 mt-1 uppercase tracking-widest font-black">AI-Driven Threat Correlation</p>
         </div>
         <div className="flex items-center gap-3">
             <button onClick={handleClientPdfExport} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-medium text-sky-400 transition-colors">
                 <Download className="w-4 h-4" /> PDF Report
             </button>
         </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-4">
              <div className={`${SECTION_BG} rounded-md p-6 flex flex-col items-center justify-center relative`}>
                    <div className="relative w-28 h-28 flex items-center justify-center">
                        <svg className="w-full h-full transform -rotate-90">
                            <circle cx="56" cy="56" r="48" stroke="#1e293b" strokeWidth="6" fill="transparent" />
                            <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="6" fill="transparent" 
                                className={`${getRiskColor(data.risk_score)} transition-all duration-1000`}
                                strokeDasharray={301} 
                                strokeDashoffset={301 - (301 * data.risk_score) / 100} 
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <span className={`text-2xl font-bold ${getRiskColor(data.risk_score)}`}>{data.risk_score}</span>
                            <span className="text-[8px] text-neutral-500 font-bold uppercase tracking-wider">Risk Index</span>
                        </div>
                    </div>
              </div>
              <IdentityProfile identities={data.suspected_identities} />
              <CountryProfile countries={data.meta?.countries} />
          </div>

          <div className="lg:col-span-2 space-y-6">
              <div className={`${SECTION_BG} rounded-md p-6`}>
                  <div className="flex items-center gap-2 mb-4">
                      <Terminal className="w-4 h-4 text-sky-400" />
                      <h3 className="text-xs font-bold text-white uppercase tracking-wide">Analyst Insight</h3>
                  </div>
                  <FormattedSummary text={data.summary} />
              </div>

              <div className={`${SECTION_BG} rounded-md overflow-hidden`}>
                  <div className="px-6 py-4 border-b border-white/5 bg-white/[0.02] flex items-center justify-between">
                      <h3 className="text-sm font-bold text-white flex items-center gap-2 uppercase tracking-wide">
                          <ShieldCheck className="w-4 h-4 text-emerald-400" /> Intelligence findings
                      </h3>
                  </div>
                  <AnomaliesTable findings={data.key_findings} />
              </div>
          </div>
      </div>

      <CriticalCredTable assets={data.critical_assets} query={query} />
    </div>
  );
}